import { NewAttachment } from 'components/Attachments/AttachmentList'
import {
  VariantCatalog,
  buildSpecsheetFilename,
  fetchSpecsheetBlob,
} from 'services/products'

/**
 * The variant fields the picker needs to identify a spec sheet. Spec sheets
 * are generated from Syndigo product data, so each one maps 1:1 to a
 * Syndigo-linked variant (`source=syndigo` on the variants catalog).
 */
export type SpecSheetVariant = Pick<VariantCatalog, 'id' | 'sku' | 'name'>

/**
 * Stable attachment filename for a variant's spec sheet. Reuses the mobile
 * share flow's naming (FIR-7498) so the same variant always produces the same
 * filename, which the picker also uses to dedupe already-attached sheets.
 */
export function specSheetFilename(variant: SpecSheetVariant): string {
  return buildSpecsheetFilename(variant)
}

/**
 * Whether a variant's spec sheet is already in the composer's attachment
 * list. Filename-based: {@link specSheetFilename} is stable per variant, so a
 * matching filename means the same sheet (the picker disables that row).
 */
export function isSpecSheetAttached(
  variant: SpecSheetVariant,
  attachedFilenames: readonly string[]
): boolean {
  return attachedFilenames.includes(specSheetFilename(variant))
}

/**
 * Fetch the generated spec-sheet PDF for a variant and wrap it as a
 * `NewAttachment`, ready for the Send Email modal's existing FormData
 * attachment path.
 */
export async function buildSpecSheetAttachment(
  variant: SpecSheetVariant
): Promise<NewAttachment> {
  const filename = specSheetFilename(variant)
  const blob = await fetchSpecsheetBlob(variant.id)
  const file = new File([blob], filename, { type: 'application/pdf' })
  return { file, filename, size: file.size }
}
